import {
  Fragment,
  FC,
  forwardRef,
  useCallback,
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
} from 'react';
import { JsonForms } from '@jsonforms/react';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import DeleteOutlined from '@material-ui/icons/DeleteRounded';
import Chip from '@material-ui/core/Chip';
import CopyToClipboard from 'react-copy-to-clipboard';
import { FileUploader } from 'react-drag-drop-files';
import FileSaver from 'file-saver';
import omit from 'lodash.omit';
import cloneDeep from 'lodash.clonedeep';

import {
  materialCells,
  materialRenderers,
} from '@jsonforms/material-renderers';
import { makeStyles } from '@material-ui/core/styles';

import menuSchema from '../menuSchema/menuSchema.json';
import menuUiSchema from '../menuSchema/menuUiSchema.json';
import submenuSchema from '../submenuSchema/submenuSchema.json';
import submenuUiSchema from '../submenuSchema/submenuUiSchema.json';

import PoweredByBlazeIT from '../PoweredByBlazeIT';
import Demo from './Demo';

const useStyles = makeStyles((_theme) => ({
  root: {
    '& > *': {
      margin: _theme.spacing(1),
      width: '25ch',
    },
  },
  container: {
    position: 'relative',
    padding: 0,
    margin: 0,
    width: '100%',
    height: '100vh',
    overflowY: 'hidden',
  },
  title: {
    textAlign: 'center',
    padding: '0.25em',
    // marginBottom: '1.2rem',
  },
  text: {
    marginBottom: '1rem',
  },
  dataContent: {
    display: 'flex',
    borderRadius: '0.25em',
    backgroundColor: '#cecece',
    padding: '1rem',
    maxWidth: '100%',
    maxHeight: 420,
    overflowY: 'auto',
  },
  actionButton: {
    display: 'block',
    marginRight: '0.5rem',
    marginBottom: '0.5rem',
  },
  demoform: {
    marginTop: '2rem',
  },
  builder: {
    padding: '1rem',
    overflowY: 'scroll',
    height: '100%',
  },
  result: {
    padding: '1rem',
    backgroundColor: '#717480',
  },
  buttonsWrapper: {
    marginLeft: '0.2rem',
  },
  stack: {},
  chip: {
    marginRight: 12,
    height: 42,
    paddingLeft: 12,
    paddingRight: 12,
  },
  input: {
    color: 'red',
  },
  white: {
    color: 'white',
  },
  fileUploaderContainer: {},
  fileUploader: {
    width: 420,
    height: '80px !important',
    border: 'solid 1px #0658c2 !important',
  },
}));

const blazeMenuInitialData = {
  items: [],
  options: {},
  submenus: {},
};

const mainMenuInitialData = {
  items: [],
  options: {},
};

const submenuInitialData = {
  items: [],
  options: {},
};

const MENU_TYPES = {
  MAIN_MENU: 'MAIN_MENU',
  SUBMENU_ID: 'SUBMENU_ID',
  SUBMENU: 'SUBMENU',
  UPLOAD_JSON: 'UPLOAD_JSON',
};

const renderers = [...materialRenderers];

const SchemaBuilder: FC<{
  goBack: () => void;
  livePreviewOn: boolean;
  setLivePreviewOn: (a: boolean) => void;
}> = ({ goBack, livePreviewOn, setLivePreviewOn }, ref) => {
  const classes = useStyles();

  const [copied, setBlazeMenuJSONCopied] = useState(false);
  const [initCopied, set3DVCodeCopied] = useState(false);

  const [displayDataAsString, setDisplayDataAsString] = useState('');

  const [blazeMenuJson, setBlazeMenuJson] = useState<any>(blazeMenuInitialData); // TODO ked sa uploadne JSON, toto treba setnut

  const [mainMenuJson, setMainMenuJson] = useState<any>(mainMenuInitialData);
  const [submenuJson, setSubmenuJson] = useState<any>(submenuInitialData);

  const [menuType, setMenuType] = useState<any>(MENU_TYPES.MAIN_MENU);

  const [submenuId, setSubmenuId] = useState('');

  const [uploadedJson, setUploadedJson] = useState('');
  const [loadFromJsonError, setLoadFromJsonError] = useState<any>('');
  const [loadJsonButtonDisabled, setLoadJsonButtonDisabled] = useState(true);

  const [detectedSubmenus, setDetectedSubmenus] = useState([]);

  useImperativeHandle(
    ref,
    () => ({
      blazeMenuJson,
    }),
    [blazeMenuJson]
  );

  useEffect(() => {
    setDisplayDataAsString(JSON.stringify(blazeMenuJson, null, 2));
    setBlazeMenuJSONCopied(false);
    set3DVCodeCopied(false);
  }, [blazeMenuJson]);

  useEffect(() => {
    // @ts-ignore:next-line
    setDetectedSubmenus(Object.keys(blazeMenuJson.submenus));
  }, [blazeMenuJson]);

  useEffect(() => {
    setBlazeMenuJson((prev: any) => ({
      ...prev,
      ...mainMenuJson,
    }));
  }, [mainMenuJson]);

  useEffect(() => {
    setBlazeMenuJson((prev: any) => {
      if (!prev.submenus[submenuJson.options.submenuId]) {
        return prev;
      }

      const updatedBlazeMenu = cloneDeep(prev);
      updatedBlazeMenu.submenus[submenuJson.options.submenuId] =
        cloneDeep(submenuJson);

      return updatedBlazeMenu;
    });
  }, [submenuJson]);

  useEffect(() => {
    if (menuType === MENU_TYPES.UPLOAD_JSON) {
      setUploadedJson('');
      setLoadJsonButtonDisabled(true);
      setLoadFromJsonError('');
    }

    if (menuType === MENU_TYPES.MAIN_MENU) {
      setSubmenuJson(submenuInitialData);
    }
  }, [menuType]);

  useEffect(() => {
    if (menuType === MENU_TYPES.UPLOAD_JSON && uploadedJson.length >= 2) {
      setLoadJsonButtonDisabled(false);
    }
  }, [uploadedJson, menuType]);

  useEffect(() => {
    const body = document.getElementsByTagName('body')[0];

    if (livePreviewOn) {
      setTimeout(() => {
        body.classList.add('freeze');
      }, 100);
    } else {
      window.scrollTo(0, 0);
      body.classList.remove('freeze');
    }
  }, [livePreviewOn]);

  const changeMenuType = (menuType: any) => () => {
    setMenuType(menuType);
  };

  const onCopyBlazeMenuJson = () => {
    set3DVCodeCopied(false);
    setBlazeMenuJSONCopied(true);
  };

  const onCopy3DVCode = () => {
    setBlazeMenuJSONCopied(false);
    set3DVCodeCopied(true);
  };

  const onLivePreview = () => {
    setLivePreviewOn(true);
  };

  const onLoadFromJson = (json: string) => {
    const confirmed = window.confirm(
      'Are you sure you have no work in progress? Once the JSON is loaded, all current changes will be lost.'
    );

    if (confirmed) {
      try {
        const parsedJson = JSON.parse(json);
        setMainMenuJson(parsedJson);
        setMenuType(MENU_TYPES.MAIN_MENU);
        setLoadFromJsonError('');
      } catch (error) {
        console.error(error);
        setLoadFromJsonError([
          'The JSON you entered is corrupted and could not be loaded. Please, make sure the JSON is valid. You can use any free JSON validator online (eg. https://jsonlint.com/)',
        ]);
      }
    }
  };

  const onSaveToFile = () => {
    try {
      const isFileSaverSupported = !!new Blob();

      const blob = new Blob([displayDataAsString], {
        type: 'text/plain;charset=utf-8',
      });
      const currentDate = new Date();

      const fileName = `${currentDate.getDate()}_${
        currentDate.getMonth() + 1
      }-${currentDate.getHours()}_${currentDate.getMinutes()} - Blaze Menu JSON`;

      FileSaver.saveAs(blob, fileName);
    } catch (e) {
      window.alert(
        "Sorry, your browser doesn't support this feature. Please, copy the result instead."
      );
    }
  };

  const onSelectMainMenu = () => {
    setMenuType(MENU_TYPES.MAIN_MENU);
  };

  const onAddSubmenu = () => {
    if (!submenuId.length) {
      window.alert('Please, enter the name of the submenu.');
      return;
    }

    const blazeMenuData = cloneDeep(blazeMenuJson);

    blazeMenuData.submenus[submenuId] = cloneDeep(submenuInitialData);
    blazeMenuData.submenus[submenuId].options.submenuId = submenuId;

    setBlazeMenuJson(blazeMenuData);

    setSubmenuJson(blazeMenuData.submenus[submenuId]);
    setSubmenuId('');
    setMenuType(MENU_TYPES.SUBMENU);
  };

  const onSelectSubmenu = (submenuId: string) => {
    if (!blazeMenuJson.submenus[submenuId]) {
      // TODO error - nieco sa pokazilo, submenu sa nenaslo
      console.error('Submenu does not exist in Blaze Menu');
      return;
    }

    // @ts-ignore:next-line
    const _submenu = cloneDeep(blazeMenuJson.submenus[submenuId]);
    _submenu.options.submenuId = submenuId;

    setSubmenuJson(_submenu);
    setSubmenuId('');
    setMenuType(MENU_TYPES.SUBMENU);
  };

  const onRemoveSubmenu = (submenuId: string) => {
    const confirmed = window.confirm(
      'Are you sure you want to remove this submenu? If you did not save it, it will be lost.'
    );

    if (confirmed) {
      const newBlazeMenuJson = cloneDeep(blazeMenuJson);
      newBlazeMenuJson.submenus = omit(blazeMenuJson.submenus, [submenuId]);
      setBlazeMenuJson(newBlazeMenuJson);

      if (
        menuType === MENU_TYPES.SUBMENU &&
        submenuJson.options.submenuId === submenuId
      ) {
        setMenuType(MENU_TYPES.MAIN_MENU);
      }
    }
  };

  const vistaCode = `window.blazeIT_BlazeMenu.createBlazeMenu(
${displayDataAsString.split('\n').join('\t\n')}
);`;

  const onGoBack = () => {
    if (menuType === MENU_TYPES.UPLOAD_JSON) {
      const confirmed = window.confirm('Do you want to leave the ');

      if (confirmed) {
        goBack();
      }
      return;
    }

    goBack();
  };

  const onFileUpload = (file: any) => {
    const reader = new FileReader();

    reader.addEventListener(
      'load',
      () => {
        if (!!reader.result && typeof reader.result === 'string') {
          onLoadFromJson(reader.result);
        }
      },
      false
    );

    reader.addEventListener('error', () => {
      setLoadFromJsonError(['The file you uploaded is not valid.']);
    });

    reader.readAsText(file);
  };

  return (
    <Fragment>
      <Grid container className={classes.container}>
        <Grid item sm={6} className={classes.builder}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <div onClick={onGoBack}>
              <img
                src='https://cdn0.iconfinder.com/data/icons/entypo/86/chevron2-512.png'
                style={{
                  transform: 'rotate(180deg)',
                  width: 20,
                  marginRight: 24,
                  paddingBottom: 6,
                  cursor: 'pointer',
                }}
              />
            </div>

            <Typography variant={'h4'} className={classes.title}>
              Create Blaze Menu
            </Typography>
          </div>

          <div style={{ marginTop: 24 }}></div>

          <div>
            {[MENU_TYPES.MAIN_MENU, MENU_TYPES.SUBMENU].includes(menuType) && (
              <Typography variant={'body1'} className={classes.text}>
                Fill the form below to obtain the code for the{' '}
                <span style={{ fontWeight: 'bold' }}>Blaze Menu</span> or load
                menu from JSON.
              </Typography>
            )}

            <Grid container justify='flex-start' spacing={1}>
              {[MENU_TYPES.MAIN_MENU, MENU_TYPES.SUBMENU].includes(
                menuType
              ) && (
                <Button
                  className={classes.actionButton}
                  onClick={changeMenuType(MENU_TYPES.UPLOAD_JSON)}
                  color='primary'
                  variant='contained'
                >
                  Load Menu from JSON
                </Button>
              )}
            </Grid>
          </div>

          {[MENU_TYPES.MAIN_MENU, MENU_TYPES.SUBMENU].includes(menuType) && (
            <>
              <div style={{ marginTop: 32 }}></div>

              <div>
                <h2>Menus:</h2>

                <p>
                  Select a menu which you want to edit. You can also create and
                  delete submenus.
                </p>

                <div className={'stack'}>
                  <Chip
                    label={'Main menu'}
                    color='primary'
                    variant={
                      menuType === MENU_TYPES.MAIN_MENU ? 'default' : 'outlined'
                    }
                    onClick={onSelectMainMenu}
                    className={classes.chip}
                  />

                  {detectedSubmenus.length > 0 && (
                    <>
                      {detectedSubmenus.map((_submenuId) => {
                        const currentSubmenuSelected =
                          submenuJson.options?.submenuId;

                        return (
                          <Chip
                            key={_submenuId}
                            label={_submenuId}
                            color='primary'
                            variant={
                              currentSubmenuSelected === _submenuId
                                ? 'default'
                                : 'outlined'
                            }
                            onClick={() => onSelectSubmenu(_submenuId)}
                            onDelete={() => onRemoveSubmenu(_submenuId)}
                            deleteIcon={
                              <div
                                className={
                                  currentSubmenuSelected === _submenuId
                                    ? 'active'
                                    : 'inactive'
                                }
                                style={{ marginLeft: 4 }}
                              >
                                <DeleteOutlined fill='secondary' />
                              </div>
                            }
                            className={classes.chip}
                          />
                        );
                      })}
                    </>
                  )}
                </div>

                <div style={{ marginTop: 24 }}></div>

                <Button
                  className={classes.actionButton}
                  onClick={changeMenuType(MENU_TYPES.SUBMENU_ID)}
                  color='primary'
                  variant='contained'
                >
                  Add submenu
                </Button>
              </div>
            </>
          )}

          <div style={{ marginTop: 32 }}></div>

          {[MENU_TYPES.MAIN_MENU, MENU_TYPES.SUBMENU].includes(menuType) && (
            <div className={classes.demoform}>
              <JsonForms
                schema={
                  menuType === MENU_TYPES.MAIN_MENU ? menuSchema : submenuSchema
                }
                uischema={
                  menuType === MENU_TYPES.MAIN_MENU
                    ? menuUiSchema
                    : submenuUiSchema
                }
                data={
                  menuType === MENU_TYPES.MAIN_MENU ? mainMenuJson : submenuJson
                }
                renderers={renderers}
                cells={materialCells}
                onChange={({ errors, data }) =>
                  menuType === MENU_TYPES.MAIN_MENU
                    ? setMainMenuJson(data)
                    : setSubmenuJson(data)
                }
              />
            </div>
          )}

          {menuType === MENU_TYPES.SUBMENU_ID && (
            <div>
              <p>
                ID of the submenu. It has to be unique. Use this ID for the
                OPEN_SUBMENU action.
              </p>

              <form className={classes.root} noValidate autoComplete='off'>
                <TextField
                  label='Submenu ID'
                  value={submenuId}
                  onChange={(event) => setSubmenuId(event.target.value)}
                  InputLabelProps={{
                    color: 'primary',
                  }}
                />
              </form>

              <div
                style={{
                  display: 'flex',
                  marginTop: 24,
                }}
              >
                <Button
                  className={classes.actionButton}
                  onClick={onAddSubmenu}
                  color='primary'
                  variant='contained'
                >
                  Add submenu
                </Button>

                <Button
                  className={classes.actionButton}
                  onClick={changeMenuType(MENU_TYPES.MAIN_MENU)}
                  color='primary'
                  variant='contained'
                >
                  Back
                </Button>
              </div>
            </div>
          )}

          {menuType === MENU_TYPES.UPLOAD_JSON && (
            <div>
              <p>Paste the JSON below:</p>

              <form className={classes.root} noValidate autoComplete='off'>
                <TextField
                  label='Blaze Menu JSON'
                  value={uploadedJson}
                  onChange={(event) => setUploadedJson(event.target.value)}
                  multiline
                  rows={10}
                  variant='filled'
                  style={{
                    width: 420,
                    maxWidth: 500,
                    margin: 0,
                  }}
                />
              </form>

              <p
                style={{
                  marginTop: 24,
                  marginBottom: 18,
                }}
              >
                or upload the file that contains the JSON (.txt or .json):
              </p>

              <div className={classes.fileUploaderContainer}>
                <FileUploader
                  handleChange={onFileUpload}
                  name='file'
                  types={['json', 'txt']}
                  classes={classes.fileUploader}
                />
              </div>

              {loadFromJsonError !== '' && (
                <div
                  style={{
                    marginTop: 12,
                    marginBottom: 12,
                    color: 'red',
                  }}
                >
                  {typeof loadFromJsonError === 'string' && (
                    <p>{loadFromJsonError}</p>
                  )}
                  {typeof loadFromJsonError !== 'string' &&
                    loadFromJsonError.map((e: any) => <p key={e}>{e}</p>)}
                </div>
              )}

              <div
                style={{
                  display: 'flex',
                  marginTop: 24,
                }}
              >
                <Button
                  className={classes.actionButton}
                  onClick={() => onLoadFromJson(uploadedJson)}
                  color='primary'
                  variant='contained'
                  disabled={loadJsonButtonDisabled}
                >
                  Load Menu
                </Button>

                <Button
                  className={classes.actionButton}
                  onClick={changeMenuType(MENU_TYPES.MAIN_MENU)}
                  color='primary'
                  variant='contained'
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </Grid>

        <Grid container sm={6} className={classes.result} direction='column'>
          <Typography
            variant={'h4'}
            className={`${classes.title} ${classes.white}`}
          >
            Generated code
          </Typography>

          <div style={{ marginTop: 24 }}></div>

          <Typography
            variant={'body1'}
            className={`${classes.text} ${classes.white}`}
          >
            <label>Blaze Menu JSON representation:</label>
          </Typography>

          <div className={classes.dataContent}>
            <pre id='boundData'>{displayDataAsString}</pre>
          </div>

          <div style={{ marginTop: 24 }}></div>

          <Grid
            container
            justify='flex-start'
            spacing={1}
            className={classes.buttonsWrapper}
          >
            <CopyToClipboard
              text={displayDataAsString}
              onCopy={onCopyBlazeMenuJson}
            >
              <Button
                className={classes.actionButton}
                onClick={() => {}}
                color='primary'
                variant='contained'
              >
                {copied ? 'JSON copied!' : 'Copy JSON'}
              </Button>
            </CopyToClipboard>

            <Button
              className={classes.actionButton}
              onClick={onSaveToFile}
              color='primary'
              variant='contained'
            >
              Save JSON to file
            </Button>

            <CopyToClipboard text={vistaCode} onCopy={onCopy3DVCode}>
              <Button
                className={classes.actionButton}
                onClick={() => {}}
                color='primary'
                variant='contained'
              >
                {initCopied ? '3DV code copied!' : 'Copy 3D Vista code'}
              </Button>
            </CopyToClipboard>

            <Button
              className={classes.actionButton}
              onClick={onLivePreview}
              color='primary'
              variant='contained'
            >
              Preview Blaze Menu
            </Button>
          </Grid>

          <div style={{ marginTop: 24 }}></div>

          <Typography
            variant={'body1'}
            className={`${classes.text} ${classes.white}`}
          >
            <strong>Copy 3D Vista code button</strong> wraps the JSON in a
            Javascript function which you can directly paste inside of the{' '}
            <strong>Execute JavaScript On Initialization action</strong>{' '}
            attached to the Main Viewer.
          </Typography>

          <div style={{ marginTop: 32 }}></div>

          <PoweredByBlazeIT color='white' />
        </Grid>
      </Grid>

      {livePreviewOn && <Demo />}
    </Fragment>
  );
};

// @ts-ignore:next-line
export default forwardRef(SchemaBuilder);
